import {createClient} from "@connectrpc/connect";
import {apiTransport} from "../api/connectTransport";
import {
	AccountAttributionService,
	SubmitAttributionRequest_AttributionChannel as Channel,
} from "../gen/kitecorp/transferify/account/v1/account_attribution_pb";
import {metaBrowserIdFor, type AdAttribution, type AttributionChannel} from "./adAttribution";

/**
 * The authenticated, write-once acquisition call (api#647), `AccountAttributionService.SubmitAttribution` on the shared
 * Connect transport (kitecorp/transferify-ui#3944). The bearer rides the transport's interceptor
 * (`api/connectTransport`), so there is no second client and no second token path.
 */

/**
 * Exactly the fields the api accepts; `capturedAt` is browser bookkeeping and never leaves the device.
 * `fbp` is Meta's browser id, read from Meta's own cookie at submission and never stored by this app
 * (kitecorp/transferify#375, `adAttribution.metaBrowserIdFor`).
 */
export type AdAttributionPayload = Omit<AdAttribution, "capturedAt"> & {fbp?: string};

/**
 * Strips the local bookkeeping, adds Meta's browser id where the record may carry it, and drops absent
 * fields, so an empty value is never sent as `null`.
 *
 * @param metaBrowserId the `_fbp` cookie's value, or null when there is none
 */
export function attributionPayload(record: AdAttribution, metaBrowserId: string | null = null): AdAttributionPayload {
	const {capturedAt: _capturedAt, ...stored} = record;
	const payload: AdAttributionPayload = {...stored, fbp: metaBrowserIdFor(record, metaBrowserId)};
	return Object.fromEntries(
		Object.entries(payload).filter(([, value]) => value !== undefined),
	) as AdAttributionPayload;
}

/** The wire values of {@link AttributionChannel} as the request names them; exhaustive, so a new value fails `tsc` here. */
const CHANNELS: Readonly<Record<AttributionChannel, Channel>> = {
	"paid-search": Channel.PAID_SEARCH,
	"paid-social": Channel.PAID_SOCIAL,
	"organic": Channel.ORGANIC,
	"ai": Channel.AI,
	"social": Channel.SOCIAL,
	"referral": Channel.REFERRAL,
	"direct": Channel.DIRECT,
	"recipient-page": Channel.RECIPIENT_PAGE,
	"recipient-page-game": Channel.RECIPIENT_PAGE_GAME,
	"recipient-page-control": Channel.RECIPIENT_PAGE_CONTROL,
	"request-page": Channel.REQUEST_PAGE,
	"referral-invite": Channel.REFERRAL_INVITE,
};

/** An absent field is left unset, which the request carries as its empty default, as the REST body left the key out. */
export async function submitAdAttribution(payload: AdAttributionPayload): Promise<void> {
	await createClient(AccountAttributionService, apiTransport)
		.submitAttribution({...payload, channel: payload.channel === undefined ? undefined : CHANNELS[payload.channel]});
}
